import { Axios } from "axios";
import { useState, useCallback, useEffect } from "react";
import { APIError } from "../core/_axios";
import { Account } from "../core/_models";
import { managementGetAccountByUniqueIdentifier, getCurrentAccountDetails } from "../core/_requests";
import { useAuth } from "../AuthContext";

export default function useAccountThatIsHydrated({
    axios,
    isForCurrentAccount = false,
    accountUniqueId = null
}: {
    axios: Axios;
    isForCurrentAccount?: boolean;
    accountUniqueId?: string | null;
}) {

    const {
        currentUserAccountUniqueId,
        currentUser
    } = useAuth();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [account, setAccount] = useState<Account | null>(null);

    const onLoadAccount = useCallback(async () => {
        if (isForCurrentAccount) {
            if (!currentUserAccountUniqueId) {
                setLoading(false);
                setLoadingError('You cannot load the current account without a valid account unique identifier.');
                return;
            }
            setLoading(true);
            setLoadingError(null);
            try {
                // For current account, we need the full Account data with public_data
                // Regular users should use the current-account endpoint, super users can use management
                let fullAccount;
                if (currentUser?.is_super_super_user || currentUser?.is_agency_admin) {
                    // Super users can use the management endpoint
                    fullAccount = await managementGetAccountByUniqueIdentifier(axios, currentUserAccountUniqueId);
                } else {
                    // Regular users use the current account endpoint
                    fullAccount = await getCurrentAccountDetails(axios);
                }
                setAccount(fullAccount);
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
                setLoadingError(errorMessage)
            } finally {
                setLoading(false)
            }
        } else if (accountUniqueId) {
            if (!accountUniqueId) {
                setLoading(false);
                setLoadingError('No account unique identifier provided.');
                return;
            }
            setLoading(true);
            setLoadingError(null);
            try {
                const updatedAccount = await managementGetAccountByUniqueIdentifier(axios, accountUniqueId);
                setAccount(updatedAccount);
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
                setLoadingError(errorMessage)
            } finally {
                setLoading(false)
            }
        } else {
            setLoading(false);
            setLoadingError('No account unique identifier provided.');
        }
    }, [axios, accountUniqueId, isForCurrentAccount, currentUserAccountUniqueId, currentUser]);

    useEffect(() => {
        onLoadAccount();
    }, [onLoadAccount]);

    return {
        loading,
        loadingError,
        account,
        setAccount,
        onLoadAccount
    };
}